import type { FeatureAccess, IOrganization, IUser } from './IUser.js'
import { FeatureAccesses, hasFeatureAccess } from './IUser.js'
import type { IOrder } from '../Order/IOrder.js'
import type { FeatureKey } from '../../Generated/Feature.js'
import { Features } from '../../Generated/Feature.js'

/**
 * Central home for **feature-named** gates.
 *
 * Call sites ask "may this user use SPIN on this order?", never "does this user have BETA?" or "does this
 * org hold INSIGHT?". A raw licence check in a view works, but loses the one thing that matters later:
 * *which feature* the check belonged to.
 *
 * So: one exported helper per gated feature, named after the feature, and views gate on that. Views
 * never call `hasFeatureAccess` or a licence helper directly for a feature gate.
 *
 * ## The table
 *
 * Which licence a feature takes is not decided here. `Generated/Feature.ts` is generated from the backend's
 * `Feature` enum, the one feature→licence table that `@RequiresFeature` endpoints use too. Every helper
 * goes through {@link canUseFeature}; route constants read `Features[…].requires`. Graduating a feature is
 * a one-line change of its `requires` in the backend enum, then `./gradlew generateTs`.
 *
 * A helper may ADD conditions on top of the table (a role, a flag, a scope kind) but never replace it, and
 * an addition is documented on the helper. `featureGating.test.ts` checks each helper against the table.
 */

/**
 * The table's verdict: the user's org holds the feature's licence, or - for a feature that
 * `inheritsFromOrder` - the order's lab shares INSIGHT ({@link hasSharedInsightLicense}). A feature with
 * no `requires` admits every signed-in user.
 *
 * `sharesInsightLicense` only says the lab holds TIER2, so an inheriting feature must require TIER2; the
 * backend's `FeatureTableTest` enforces that.
 */
export const canUseFeature = (feature: FeatureKey, user?: IUser | null, order?: Pick<IOrder, 'sharesInsightLicense'> | null): boolean => {
  const { requires, inheritsFromOrder } = Features[feature]
  if (!requires) return !!user
  const own = !!user && hasFeatureAccess(user.org, requires)
  return own || (inheritsFromOrder && requires === FeatureAccesses.TIER2 && hasSharedInsightLicense(order))
}

/**
 * Picking a SPIN algorithm other than the preferred one (`PREFERRED_SPIN_ALGORITHM`, SPCFY). Without it,
 * no SWARM/VSEARCH reference is shown: `ReportSettingsModal` hides its algorithm select and always sends
 * the preferred algorithm, and the `OtuDataTableView` and `SpinPanel` algorithm selects narrow the
 * scope's list to the preferred one (a `?spinAlgorithm=` link falls back to it too).
 *
 * The backend still serves every algorithm on the cube and SPIN metadata endpoints; only the report
 * endpoint checks this gate, for a non-SPCFY `spinAlgorithm`.
 */
export const canChooseSpinAlgorithm = (user?: IUser | null): boolean => canUseFeature('SPIN_ALGORITHM_CHOICE', user)

/**
 * Editing a generated pool CSV in an in-app grid instead of re-uploading a file. Frontend-only: the edit is
 * submitted through the ordinary CSV upload endpoints.
 */
export const canUseCsvGridEditor = (user?: IUser | null): boolean => canUseFeature('CSV_GRID_EDITOR', user)

/** The order wizard (`/orderWizard`) and its entry points: a removable preview. Frontend-only. */
export const canUseOrderWizard = (user?: IUser | null): boolean => canUseFeature('ORDER_WIZARD', user)

/**
 * `/colorcode` — shows which chart colour a taxon or trait name maps to. Frontend-only; no backend
 * endpoint shares the gate.
 */
export const colorCodeRouteFeatureAccess: FeatureAccess | undefined = Features.COLOR_CODE.requires

// ---------------------------------------------------------------------------
// Visualisation and projects
// ---------------------------------------------------------------------------

type OrderLicence = Pick<IOrder, 'sharesInsightLicense'> | null | undefined

/**
 * The OTU table of an order: the order menu's "View OTUs/ASVs" entry. Order-scoped, so a lab-shared
 * licence counts.
 *
 * Backend (`Feature.OTU_TABLE`): the OTU cube `/api/vis/otu/{scopeId}/…` and
 * `GET /api/orders/{orderId}/otu/otus/{blastType}/autocomplete`, both inherited from the order's lab.
 */
export const canUseOtuTableOnOrder = (order?: OrderLicence, user?: IUser | null): boolean => canUseFeature('OTU_TABLE', user, order)

/**
 * The Krona plot of an order: the order menu's "Show Krona Plot". Order-scoped, so a lab-shared licence counts.
 *
 * Backend (`Feature.KRONA`): `/api/vis/krona/{scopeId}/{cube,treestructure,dataset,datasets}`, inherited from
 * the order's lab.
 */
export const canUseKronaOnOrder = (order?: OrderLicence, user?: IUser | null): boolean => canUseFeature('KRONA', user, order)

/**
 * Trait overlays on an order's visualisations. Order-scoped, so a lab-shared licence counts.
 *
 * Backend (`Feature.TRAITS`): `GET /api/vis/krona/{scopeId}/traits`, inherited from the order's lab.
 */
export const canUseTraitsOnOrder = (order?: OrderLicence, user?: IUser | null): boolean => canUseFeature('TRAITS', user, order)

/**
 * An order's visualisations as a whole: the order menu's INSIGHT group, and the order visualisation
 * workspace, which draws on the OTU cube, Krona and traits. Any one of those features opens it; each
 * endpoint still checks its own.
 */
export const canUseVisualisationOnOrder = (order?: OrderLicence, user?: IUser | null): boolean =>
  canUseOtuTableOnOrder(order, user) || canUseKronaOnOrder(order, user) || canUseTraitsOnOrder(order, user)

/**
 * Evaluation projects: the project pages and their workspace, the sidebar entry, project invites and the
 * dashboard's recent projects. A project has no lab to inherit from, so only the user's own licence counts.
 *
 * Backend (`Feature.PROJECTS`): `/api/projects/…` - `ProjectController`, `ProjectSampleController`,
 * `ProjectOrderController`, `ProjectEvaluationController` and `OtuSearchController`.
 */
export const canUseProjects = (user?: IUser | null): boolean => canUseFeature('PROJECTS', user)

/** {@link canUseProjects} for `ProtectedRoute`'s `featureAccessRequired` and the sidebar entry. */
export const projectsRouteFeatureAccess: FeatureAccess | undefined = Features.PROJECTS.requires

/**
 * Saved workspace views and templates: the workspace's "Views" button and `?remoteView=` links. Never
 * inherited from an order's lab.
 *
 * Backend (`Feature.SAVED_VIEWS`): `/api/workspace-states/…` (`WorkspaceStateController`).
 */
export const canUseSavedViews = (user?: IUser | null): boolean => canUseFeature('SAVED_VIEWS', user)

// ---------------------------------------------------------------------------
// spcfy INSIGHT (TIER2) - licence helpers, for where the question really is the licence
// ---------------------------------------------------------------------------

/**
 * Does this organisation hold INSIGHT? Decided by the Chargebee entitlements alone, as on the backend
 * (`FeatureAccessResolver`); the plan id is for display, never for gating.
 */
const hasInsightLicense = (org?: IOrganization | null): boolean => !!org && hasFeatureAccess(org, FeatureAccesses.TIER2)

/** The user's own organisation holds an INSIGHT licence. */
export const hasOwnInsightLicense = (user?: IUser | null): boolean => hasInsightLicense(user?.org)

/**
 * The order's processing lab has shared its INSIGHT licence with whoever is looking at that order.
 * This is a real, advertised entitlement — the order view tells the customer "Your processing lab
 * has shared its INSIGHT license for this order" — so it must grant the same things a direct
 * licence does.
 *
 * The verdict is the backend's: the lab's Chargebee fields are not serialized to a counterparty, so
 * `hasInsightLicense` cannot be applied to it here. `FeatureAccessResolver` decides it, the same way it
 * decides an order endpoint's lab fallback.
 */
export const hasSharedInsightLicense = (order?: Pick<IOrder, 'sharesInsightLicense'> | null): boolean =>
  order?.sharesInsightLicense ?? false

// ---------------------------------------------------------------------------
// SPIN (canonical species clusters)
// ---------------------------------------------------------------------------

/**
 * SPINs in this order's data: the OTU table's SPIN toggle, the visualisation SPIN panel, the SPIN status
 * label and the report-options entry with its SPIN report. Order-scoped, so a lab-shared licence counts.
 *
 * Backend (`Feature.SPIN`): `/api/vis/spin/{scopeId}/*`, inherited from the order's lab, and
 * `POST /api/orders/{orderId}/report` with SPIN settings (`requireForReport`).
 */
export const canUseSpinOnOrder = (order?: Pick<IOrder, 'sharesInsightLicense'> | null, user?: IUser | null): boolean =>
  canUseFeature('SPIN', user, order)

/**
 * SPINs in the user's own data - projects, and the nudge on the public SPIN pages.
 *
 * Backend (`Feature.SPIN`): `/api/vis/spin/{scopeId}/*` on a project scope, with no lab to inherit from,
 * and `POST /api/projects/{projectId}/report` with SPIN settings (`requireForReport`).
 */
export const canUseSpin = (user?: IUser | null): boolean => canUseFeature('SPIN', user)

/**
 * The public SPIN pages (`/spins`, `/spins/:spinId`) and their sidebar entry, for `ProtectedRoute`'s
 * `featureAccessRequired`. `undefined` admits every signed-in user, as `GET /api/spins/{spinId}` and
 * its `/lineage` do.
 */
export const spinRouteFeatureAccess: FeatureAccess | undefined = Features.SPIN_LOOKUP.requires
